// ============================================================
// School Switcher Component
// ============================================================
// Advanced dropdown for Owners to switch between schools.
// Includes search, school logo, name, and student count.

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  InputBase,
  Divider,
} from '@mui/material';
import { ChevronDown, Search, Users, Building2 } from 'lucide-react';
import { FcLibrary } from 'react-icons/fc';
import { useSchoolSwitcher } from '@/hooks';
import { tokens } from '@/theme/tokens';

export function SchoolSwitcher({ fallbackTitle }) {
  const navigate = useNavigate();
  const { schools, currentSchool, switchSchool, canSwitchSchool } = useSchoolSwitcher();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSchools = useMemo(() => {
    if (!searchQuery) return schools;
    return schools.filter(s => {
      const sName = s.name || s.school_name || '';
      return sName.toLowerCase().includes(searchQuery.toLowerCase()) || 
        (s.city && s.city.toLowerCase().includes(searchQuery.toLowerCase()));
    });
  }, [schools, searchQuery]);

  if (!canSwitchSchool) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ml: 1.5,
          px: 2,
          py: 0.5,
          backgroundColor: tokens.colors.primary[50],
          borderRadius: tokens.radius.full,
          border: `1px solid ${tokens.colors.primary[100]}`,
        }}
      >
        <FcLibrary size={20} />
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ color: tokens.colors.primary.main, letterSpacing: 0.5 }}
        >
          {fallbackTitle || 'School Portal'}
        </Typography>
      </Box>
    );
  }
  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  
  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleSelect = (schoolId) => {
    switchSchool(schoolId);
    handleClose();
    navigate('/dashboard');
  };

  const handleViewAllSchools = () => {
    handleClose();
    navigate('/schools');
  };

  const currentSchoolName = currentSchool ? (currentSchool.name || currentSchool.school_name) : null;

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ml: 1.5,
          px: 2,
          py: 0.5,
          backgroundColor: tokens.colors.primary[50],
          borderRadius: tokens.radius.full,
          border: `1px solid ${tokens.colors.primary[100]}`,
          cursor: 'pointer',
          minWidth: 150,
          '&:hover': {
            backgroundColor: tokens.colors.primary[100],
          },
        }}
      >
        <FcLibrary size={20} />
        <Typography
          variant="subtitle2"
          fontWeight={700}
          noWrap
          sx={{ flex: 1, maxWidth: 200, color: tokens.colors.primary.main, letterSpacing: 0.5 }}
        >
          {currentSchoolName ? currentSchoolName : (fallbackTitle || 'Switch School')}
        </Typography>
        <ChevronDown size={16} color={tokens.colors.primary.main} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              mt: 1,
              p: 0,
              maxHeight: 440,
            },
          },
        }}
      >
        {/* Search Header */}
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Search size={16} color={tokens.colors.text.muted} />
          <InputBase
            placeholder="Search school or city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, fontSize: tokens.typography.sizes.body }}
            autoFocus
          />
        </Box>
        <Divider />

        {/* School List */}
        <Box sx={{ overflowY: 'auto', maxHeight: 280 }}>
          {filteredSchools.length > 0 ? (
            filteredSchools.map((school) => {
              const nameStr = school.name || school.school_name || 'School';
              return (
                <MenuItem
                  key={school.id}
                  onClick={() => handleSelect(school.id)}
                  selected={currentSchool?.id === school.id}
                  sx={{
                    py: 1.5,
                    px: 2,
                    gap: 1.5,
                    alignItems: 'center',
                    '&.Mui-selected': {
                      backgroundColor: tokens.colors.primary[50],
                    },
                  }}
                >
                  {/* School Logo/Icon */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <FcLibrary size={24} />
                  </Box>
                  
                  {/* School Info */}
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {nameStr} ({school.city || 'N/A'})
                    </Typography>
                    {(school.studentCount !== undefined && school.studentCount !== null) ||
                    (school.student_count !== undefined && school.student_count !== null) ||
                    (school.total_students !== undefined && school.total_students !== null) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <Users size={12} color={tokens.colors.text.muted} />
                        <Typography variant="caption" color="text.secondary">
                          {(school.studentCount ?? school.student_count ?? school.total_students).toLocaleString()} students
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                </MenuItem>
              );
            })
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No schools found
              </Typography>
            </Box>
          )}
        </Box>

        {/* View All Schools Action Link */}
        <Divider />
        <MenuItem
          onClick={handleViewAllSchools}
          sx={{
            py: 1.5,
            px: 2,
            gap: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: tokens.colors.grey[50],
            '&:hover': {
              backgroundColor: tokens.colors.primary[50],
            },
          }}
        >
          <FcLibrary size={22} />
          <Typography variant="body2" fontWeight={700} color="primary.main">
            View All Schools
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
